import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

/**
 * Themed dropdown + combobox for the chat composers (crop advisor + internal assistant). These replace
 * the native <select> and <input list=datalist>, whose OS-rendered popups can't be themed and looked
 * foreign against the panel. Self-contained Tailwind (no app `ui/` kit) so they render identically in
 * the authed shell (light/dark) AND on the public marketing site — matching ChatShell's approach.
 *
 * Both open UPWARD (`bottom-full`) because the composer is pinned to the bottom of the chat panel.
 */

// Shared popup + option classes so the two controls stay visually identical. `gb-menu-scroll` gives a
// slim themed scrollbar (the default 10px pill reads too heavy in a small menu); each list sets its own
// max-height — the language list is sized to fit all options, so no scrollbar shows there at all.
const POPUP =
  'gb-menu-scroll absolute bottom-full z-30 mb-1 overflow-y-auto rounded-lg border border-line bg-surface py-1 shadow-[0_10px_30px_rgba(0,0,0,0.18)] dark:bg-[#232a22]';
const optionCls = (active: boolean, selected: boolean) =>
  [
    'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
    active ? 'bg-surface-2' : '',
    selected ? 'font-medium text-brand-700 dark:text-brand-300' : 'text-muted',
  ].join(' ');

/** Close `open` when a mousedown lands outside `ref`. */
function useOutsideClose(ref: React.RefObject<HTMLElement>, open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) close(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, ref, close]);
}

interface Option { value: string; label: string }

/** Compact themed dropdown (language picker). Shows the selected label + chevron. */
export function LangSelect({
  value, onChange, options, ariaLabel, className = '',
}: { value: string; onChange: (v: string) => void; options: Option[]; ariaLabel?: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const wrap = useRef<HTMLDivElement>(null);
  useOutsideClose(wrap, open, () => setOpen(false));
  const current = options.find((o) => o.value === value) ?? options[0];

  const pick = (v: string) => { onChange(v); setOpen(false); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) { setOpen(true); setHi(options.findIndex((o) => o.value === value)); } else setHi((h) => Math.min(h + 1, options.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); return; }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (open && hi >= 0) pick(options[hi].value); else setOpen((o) => !o); }
  };

  return (
    <div ref={wrap} className={`relative shrink-0 ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKey}
        className="flex w-full items-center justify-between gap-1 rounded-lg border border-line bg-surface px-2 py-2 text-xs text-content outline-none transition focus:border-brand-500"
      >
        <span className="truncate">{current?.label}</span>
        <ChevronDown size={14} className={`shrink-0 text-subtle transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul role="listbox" className={`${POPUP} right-0 max-h-96 min-w-30`}>
          {options.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
              className={optionCls(i === hi, o.value === value)}
            >
              <span className="flex-1 truncate">{o.label}</span>
              {o.value === value && <Check size={14} className="shrink-0" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Themed searchable combobox (crop picker). Free-text is allowed (the farmer can type a crop we don't
 * list); the dropdown filters `options` as they type. Enter with a highlighted option selects it;
 * Enter otherwise calls `onSubmit` (so typing a crop + Enter still fires the query, as before).
 */
export function CropCombobox({
  value, onChange, options, placeholder, onSubmit, ariaLabel, className = '',
}: {
  value: string; onChange: (v: string) => void; options: string[]; placeholder?: string;
  onSubmit: () => void; ariaLabel?: string; className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const wrap = useRef<HTMLDivElement>(null);
  useOutsideClose(wrap, open, () => setOpen(false));

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    const list = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
    return list.slice(0, 60);
  }, [options, value]);

  const pick = (v: string) => { onChange(v); setOpen(false); setHi(-1); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) setOpen(true); else setHi((h) => Math.min(h + 1, filtered.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); return; }
    if (e.key === 'Escape') { setOpen(false); setHi(-1); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && hi >= 0 && filtered[hi]) pick(filtered[hi]);
      else { setOpen(false); onSubmit(); }
    }
  };

  return (
    <div ref={wrap} className={`relative min-w-0 flex-1 ${className}`}>
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle" />
        <input
          value={value}
          aria-label={ariaLabel}
          aria-expanded={open}
          role="combobox"
          aria-autocomplete="list"
          onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="w-full rounded-lg border border-line bg-surface py-2 pl-8 pr-3 text-sm text-content outline-none transition focus:border-brand-500"
        />
      </div>
      {open && filtered.length > 0 && (
        <ul role="listbox" className={`${POPUP} left-0 right-0 max-h-56`}>
          {filtered.map((o, i) => (
            <li
              key={o}
              role="option"
              aria-selected={o === value}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              className={optionCls(i === hi, o === value)}
            >
              <span className="flex-1 truncate">{o}</span>
              {o === value && <Check size={14} className="shrink-0" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
